/**
 * ActivityUI - Main UI orchestrator for all activity types.
 *
 * Generalized from bumblebee ChallengeUI. Composes shared UI components
 * and delegates type-specific rendering to the active plugin.
 *
 * @see GitHub Issue #852
 */

import FeedbackModal from './FeedbackModal';
import HintManager from './HintManager';
import ScoringEngine from './ScoringEngine';
import AchievementEngine from './AchievementEngine';
import InactivityMonitor from './InactivityMonitor';
import ActivityAudio from './ActivityAudio';

export default class ActivityUI {
    /**
     * @param {object} activityData - Validated activity definition
     * @param {object} stateMachine - ActivityStateMachine instance
     * @param {object} plugin - Active activity type plugin
     * @param {HTMLElement} container - Editor container
     * @param {object} callbacks - {onNext, onReplay, onComplete}
     */
    constructor (activityData, stateMachine, plugin, container, callbacks) {
        this._activity = activityData;
        this._sm = stateMachine;
        this._plugin = plugin;
        this._container = container;
        this._callbacks = callbacks || {};
        this._isStoryWalk = Boolean(activityData && activityData.storyWalk);
        this._hasIntegratedFlow = Boolean(activityData && (activityData.storyWalk || activityData.bugBand || activityData.moonwalkMachine));

        // Shared components
        this._feedbackModal = new FeedbackModal();
        this._hintManager = null;
        this._inactivityMonitor = null;

        this._init();
    }

    _init () {
        // Set up hint manager
        const hints = (this._activity.scaffolding && this._activity.scaffolding.hints) || [];
        this._hintManager = new HintManager(hints, {
            onFrustration: () => {
                // Auto-show first available hint on frustration
                const hint = this._hintManager.nextHint();
                if (hint) this._showHint(hint);
            },
            onGiveUp: () => {
                // Show walkthrough
                this._showWalkthrough();
            }
        });

        // Set up inactivity monitor
        if (!this._hasIntegratedFlow) {
            this._inactivityMonitor = new InactivityMonitor(
                this._container,
                () => this._handleInactivity()
            );
        }

        // Listen to state changes
        this._sm.onStateChange((prev, next, meta) => {
            this._onStateChange(prev, next, meta);
        });
    }

    _onStateChange (prev, next, meta) {
        // Delegate to plugin
        if (this._plugin && typeof this._plugin.onStateChange === 'function') {
            this._plugin.onStateChange(prev, next, meta);
        }

        // Handle terminal states
        switch (next) {
        case 'READY':
            if (this._inactivityMonitor) {
                this._inactivityMonitor.start();
            }
            break;
        case 'SUCCESS':
            this._handleSuccess();
            break;
        case 'NEEDS_WORK':
        case 'FAILED':
            this._handleNeedsWork();
            break;
        case 'GUIDED_STEP':
            if (this._inactivityMonitor) {
                this._inactivityMonitor.resetTimer();
            }
            break;
        case 'FREE_PLAY':
            if (this._inactivityMonitor) {
                this._inactivityMonitor.resetTimer();
            }
            ActivityAudio.play('confetti');
            break;
        }
    }

    _handleSuccess () {
        if (this._inactivityMonitor) {
            this._inactivityMonitor.stop();
        }

        // Calculate score
        const evalResult = this._plugin.evaluate ? this._plugin.evaluate() : {stepsCompleted: 0};
        const stars = ScoringEngine.calculateStars(
            this._activity.type,
            evalResult,
            this._activity.successCriteria
        );

        // Check for new badges
        const newBadges = AchievementEngine.evaluate({
            activityId: this._activity.activityId,
            activityType: this._activity.type,
            stars: stars,
            attempts: this._hintManager.failures + 1,
            hintsUsed: this._hintManager.currentTier + 1,
            progress: {}, // Will be populated from storage
            earnedBadges: []
        });

        // StoryWalk activities show their own completion modal — skip the old one
        // Skip old FeedbackModal for all integrated activity flows — they show their own completion UI
        if (this._hasIntegratedFlow) {
            // Completion handled by StoryWalkFlow / BugBandFlow / MoonwalkMachineFlow
        } else {
            this._feedbackModal.showSuccess({
                stars: stars,
                message: stars === 3 ? 'Amazing!' : stars === 2 ? 'Great job!' : 'You did it!',
                onReplay: () => {
                    if (this._callbacks.onReplay) this._callbacks.onReplay();
                },
                onNext: this._callbacks.onNext ? () => {
                    this._callbacks.onNext();
                } : null
            });
        }

        // Notify for persistence
        if (this._callbacks.onComplete) {
            this._callbacks.onComplete({
                activityId: this._activity.activityId,
                stars: stars,
                attempts: this._hintManager.failures + 1,
                hintsUsed: this._hintManager.currentTier + 1,
                newBadges: newBadges,
                evalResult: evalResult
            });
        }
    }

    _handleNeedsWork () {
        if (this._hasIntegratedFlow) {
            this._sm.transition('BUILDING');
            return;
        }

        this._hintManager.recordFailure();

        this._feedbackModal.showNeedsWork({
            message: 'Almost! Try again!',
            onRetry: () => {
                this._hintManager.resetForRetry();
                const type = this._activity.type;
                if (type === 'challenge') {
                    this._sm.transition('BUILDING');
                } else if (type === 'guided-creation') {
                    this._sm.transition('GUIDED_STEP', {stepIndex: 0});
                } else {
                    this._sm.transition('ACTIVE');
                }
            }
        });
    }

    _handleInactivity () {
        if (!this._hintManager) {
            return;
        }
        if (this._hintManager.hintsUnlocked && this._hintManager.hasMoreHints()) {
            const hint = this._hintManager.nextHint();
            if (hint) this._showHint(hint);
        }
    }

    _showHint (hint) {
        ActivityAudio.play('hint');
        // Delegate hint display to plugin (type-specific rendering)
        if (this._plugin && typeof this._plugin.showHint === 'function') {
            this._plugin.showHint(hint);
        }
    }

    _showWalkthrough () {
        if (this._plugin && typeof this._plugin.showWalkthrough === 'function') {
            this._plugin.showWalkthrough();
        }
    }

    recordReset () {
        if (this._hintManager) {
            this._hintManager.recordReset();
        }
    }

    destroy () {
        if (this._feedbackModal) this._feedbackModal.destroy();
        if (this._hintManager) this._hintManager.destroy();
        if (this._inactivityMonitor) this._inactivityMonitor.destroy();
    }
}
